import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { deriveCapabilities, CAP } from '../lib/planAccess';
import { plural } from '../lib/labels';
import { num0, num1 } from '../lib/numeros';
import { MARGEN, NOMBRE_MACRO } from '../lib/exceso';
import { textoDesvio } from '../components/nutrition/DiaVacio';
import DiasQueEntrenas from '../components/DiasQueEntrenas';
import CampoDePeso from '../components/CampoDePeso';
import { Dumbbell, Moon, Utensils, Waves, ChevronLeft, ChevronRight, HelpCircle, Bell, BellOff } from 'lucide-react';

// Mi semana (tarea 5.1 del rediseño, doc de Jesús del 21-08): los siete días de la
// semana actual, cada uno con su dieta y su entreno. Es la pantalla para programar la
// víspera: de aquí se salta a Nutrición en el día que toque (?date=, el mismo camino
// que ya usa NutritionPage).

// El servidor manda lunes a domingo en este orden; la etiqueta corta sale del índice,
// no de parsear la fecha con la zona del navegador.
const DIAS_CORTOS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const diaDelMes = (fecha) => Number(String(fecha || '').slice(8, 10)) || '';

// Mismo marco que RoutinePage, y fuera del componente por lo mismo que allí: definido
// dentro, cada render crearía un componente nuevo y React remontaría la página.
const Wrap = ({ children }) => (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1200px] mx-auto animate-fade-in" data-testid="mi-semana-page">{children}</div>
);

// ── LA SEMANA QUE SE ESTÁ MIRANDO (fichas 03, 04 y 15 del artefacto «Mi semana») ──────

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun',
                      'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** «31 ago - 6 sep», y «1 - 7 sep» cuando los dos días caen en el mismo mes.
 *
 *  El mes se repite solo cuando cambia: en «1 sep - 7 sep» la palabra sobra las dos veces y
 *  el rango se lee peor. Se parte la fecha a mano y no con `Date`, que en un ISO sin hora
 *  se va al día anterior según la zona del navegador (la trampa de siempre).
 */
const tramoDeLaSemana = (desde, hasta) => {
    if (!desde || !hasta) return '';
    const [, m0, d0] = desde.split('-');
    const [, m1, d1] = hasta.split('-');
    const dia = (d) => Number(d);
    const mes = (m) => MESES_CORTOS[Number(m) - 1] || '';
    return m0 === m1
        // SIN GUIÓN LARGO, que en esta casa no se usan en ninguna pantalla.
        ? `${dia(d0)} - ${dia(d1)} ${mes(m1)}`
        : `${dia(d0)} ${mes(m0)} - ${dia(d1)} ${mes(m1)}`;
};

// El menos de verdad (U+2212) y no un guion, como en el resto de la app.
/** «Semana 2 de 12 de tu plan · semana 3 de 4 de tu ciclo» (ficha 07 del artefacto «Mi
 *  semana», Jesús 6-09-2026, con el vocabulario del 18-09-2026).
 *
 *  LOS DOS RELOJES, CON SU NOMBRE. La ficha de Jesús escribía «de 12 de tu ciclo · de 4 de
 *  tu rutina», y ese vocabulario se cambia hoy por decisión de Francisco («manda César»):
 *
 *      LAS SEMANAS DEL PLAN   son las que ha pagado: 12, o 4, o las que compró. Las manda
 *                             Stripe, corren solas y no se paran nunca.
 *      EL CICLO               son cuatro semanas, un bloque de reporte. Lo manda Jesús
 *                             cuando contesta, y se para si el cliente no manda el suyo.
 *
 *  Llamarlos a los dos «semana» es lo que hacía que una ficha dijera «semana 10 de 12» y
 *  «te queda 1 día de ciclo» a la vez sin que nadie supiera cuál de las dos mentía.
 *
 *  Cada mitad sale solo si se sabe, y con su total solo si también se sabe: al que no tiene
 *  ciclo abierto o no tiene rutina no se le inventa por cuál va. Si no se sabe ninguna de
 *  las dos, no hay frase y la línea no ocupa sitio.
 *
 *  SIN EL NÚMERO DE LA RUTINA. Su ficha 07 lo pide («de tu rutina 34») y su ficha 14 dice
 *  que el número es del equipo y que vale para todas las pantallas. Manda la 14, que es la
 *  que él marcó como trabajo.
 */
const enQueSemanaEsta = (s) => {
    // CON `== null`, QUE EL 0 ES UN NUMERO (Francisco, 11-09: «debería decir semana 0
    // de... en mi semana también»). Con `if (!n)` la Semana 0 -- el que ya entró pero
    // arranca el lunes -- se trataba como «no se sabe» y la línea salía vacía.
    const tramo = (n, total, texto) => {
        if (n == null) return null;
        return total ? `${texto} ${n} de ${total}` : `${texto} ${n}`;
    };
    // OJO CON LOS NOMBRES DE LOS CAMPOS: `semana_ciclo` trae las semanas DEL PLAN y
    // `semana_rutina` las del CICLO. Los campos se llaman así desde antes de que se fijara
    // el vocabulario y renombrarlos toca el servidor, el panel y los avisos; lo que el
    // cliente lee sí está bien desde hoy.
    const delPlan = tramo(s.semana_ciclo, s.semanas_ciclo, 'Semana');
    const delCiclo = tramo(s.semana_rutina, s.semanas_rutina, 'semana');

    // LOS PLANES MENSUALES NO TIENEN CICLO (César, 19-09). Su captura: una cliente de El
    // Lunes Empiezo con «Semana 1 de 4 de tu plan · semana 1 de 4 de tu ciclo», que además
    // de sobrar la segunda mitad, decía dos veces lo mismo. En esos planes no hay rutina y
    // no hay bloque de cuatro semanas: lo suyo es la fecha de pago. Lo dice el servidor.
    const sinCiclo = s.lleva_ciclo === false;
    // Y EN EL PLAN MENSUAL TAMPOCO VA LA DEL PLAN (Trello de César, 24-09: «no debería
    // salir la semana que va»). Se paga mes a mes: «Semana 3 de 4 de tu plan» no le dice
    // nada y parece que el plan se le acaba. Manda lo más reciente sobre lo del 19-09.
    if (sinCiclo) return '';

    // Y AUNQUE LO LLEVE, SI LAS DOS MITADES DICEN LO MISMO SE DICE UNA VEZ. «Semana 1 de 4
    // de tu plan · semana 1 de 4 de tu ciclo» no informa de nada: son dos relojes distintos
    // y el valor de tenerlos juntos es justamente verlos ir por sitios distintos.
    const iguales = delPlan && delCiclo
        && delPlan.toLowerCase() === delCiclo.toLowerCase();

    return [
        delPlan && `${delPlan} de tu plan`,
        !sinCiclo && !iguales && delCiclo && `${delCiclo} de tu ciclo`,
    ].filter(Boolean).join(' · ');
};

/** El día de un hito en corto («Mar», «Vie»), que es como él los escribe en el esquema. */
const diaCortoDe = (fecha) => {
    const d = new Date(`${fecha}T12:00:00`);
    return DIAS_CORTOS[(d.getDay() + 6) % 7];
};

const MENOS = '−';
/** El peso de una semana, con su signo. El cero no lleva signo, así que no se escribe como
 *  número: «0 kg» al lado de un «−1,8 kg» se lee como si fuera otro dato del mismo tipo, y
 *  lo que dice en realidad es que la semana no se movió. Se dice con palabras.
 *  Vale para las dos frases donde sale: «... en la semana» y «La anterior: ...». */
const textoKg = (kg) => {
    if (kg == null) return null;
    if (Math.abs(kg) < 0.05) return 'sin cambio';
    const n = num1(Math.abs(kg));
    return `${kg > 0 ? '+' : MENOS}${n} kg`;
};

/** Los cuatro números de una semana cerrada, en el orden de su maqueta.
 *
 *  «Entrenos hechos» solo sale cuando este cliente registra sus sesiones Y la semana tenía
 *  alguno previsto: es la misma regla del punto 59 del 24-08 que gobierna el titular de la
 *  semana en curso, y por lo mismo, un «0 de 0» no es un dato.
 */
const cifrasDelCierre = (c) => {
    const filas = [
        { id: 'creados', que: 'días creados', valor: `${c.creados} de ${c.dias}` },
        // «DÍAS CUADRADOS», con su palabra delante, que es como lo escribe el artefacto:
        // «días creados, días cuadrados, días cerrados, entrenos hechos». Puesto a secas,
        // entre «días creados» y «días cerrados», se lee como si contara otra cosa.
        { id: 'cuadrados', que: 'días cuadrados', valor: `${c.cuadrados} de ${c.dias}` },
        { id: 'cerrados', que: 'días cerrados', valor: `${c.cerrados} de ${c.dias}` },
    ];
    if (c.entrenos_total && c.entrenos_hechos != null) {
        filas.push({ id: 'entrenos', que: 'entrenos hechos',
                     valor: `${c.entrenos_hechos} de ${c.entrenos_total}` });
    }
    return filas;
};

/** «−0,6 kg en la semana · 3 pesajes · 2 extras apuntados».
 *
 *  Cada trozo sale solo si tiene algo que decir: sin pesarse no se escribe «0 pesajes», que
 *  es contarle lo que no hizo. Con un solo pesaje se dice el pesaje y no los kilos, porque
 *  con uno no hay movimiento que contar y un «0,0 kg» se leería como que no se movió.
 */
const loQueMovio = (c) => [
    c.kg != null ? `${textoKg(c.kg)} en la semana` : null,
    c.pesajes ? plural(c.pesajes, 'pesaje') : null,
    c.extras ? `${plural(c.extras, 'extra')} ${c.extras === 1 ? 'apuntado' : 'apuntados'}` : null,
].filter(Boolean).join(' · ');

/** «¿Y la semana entera, cómo la ves?» (fichas 12 y 17 del artefacto «Mi semana»).
 *
 *  UNA SOLA PREGUNTA NUEVA, que es lo que él pide: «lo importante: no se le vuelve a
 *  preguntar lo que ya contestó. El check-in del día ya le pide estrellas y tres notas; el
 *  domingo se las enseña hechas y solo le pide una estrella más».
 *
 *  Y NO ES OBLIGATORIA (ficha 17): «si no la rellena, la semana se cierra igual y no le dice
 *  nada nadie». Por eso no hay aviso, ni rojo, ni nada que insista. Si ya la mandó, se le
 *  enseña lo que puso y se le deja cambiarla.
 */
const ValoracionDeLaSemana = ({ semana, api, onGuardada, hayResumenArriba = false }) => {
    const guardada = semana.valoracion;
    const [estrellas, setEstrellas] = useState(guardada?.estrellas || 0);
    const [nota, setNota] = useState(guardada?.nota || '');
    const [guardando, setGuardando] = useState(false);
    const [hecho, setHecho] = useState(Boolean(guardada));

    const guardar = async () => {
        if (!estrellas || guardando) return;
        setGuardando(true);
        try {
            await api.post('/diets/semana/valoracion',
                           { inicio: semana.inicio, estrellas, nota });
            setHecho(true);
            if (onGuardada) onGuardada();
        } catch (e) {
            toast.error('No hemos podido guardar tu valoración. Inténtalo otra vez.');
        } finally { setGuardando(false); }
    };

    return (
        <div className="surface p-4 mb-4" data-testid="valoracion-semana">
            {/* EL TÍTULO, SOLO SI NO ESTÁ YA ARRIBA (12-09). El domingo el resumen se
                titula «Cómo fue tu semana», y esta caja repetía la misma frase tres
                centímetros más abajo. En el artefacto hay un título y luego la pregunta,
                que es lo que se lee de corrido. */}
            {!hayResumenArriba && <p className="caption mb-2">Cómo fue tu semana</p>}
            <p className="text-sm text-foreground mb-3">¿Y la semana entera, cómo la ves?</p>
            <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button" onClick={() => { setEstrellas(n); setHecho(false); }}
                        aria-label={`${n} de 5`} data-testid={`estrella-semana-${n}`}
                        className={`text-2xl leading-none transition-colors ${
                            n <= estrellas ? 'text-brand' : 'text-muted-foreground/40 hover:text-muted-foreground'}`}>
                        {n <= estrellas ? '★' : '☆'}
                    </button>
                ))}
            </div>
            <textarea value={nota} onChange={(e) => { setNota(e.target.value); setHecho(false); }}
                rows={2} maxLength={1000}
                placeholder="Si quieres contar algo, aquí (opcional)"
                data-testid="nota-semana"
                className="w-full bg-muted rounded-xl px-3 py-2 text-sm text-foreground
                           placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand" />
            <div className="flex items-center gap-3 mt-3">
                <button type="button" onClick={guardar} disabled={!estrellas || guardando || hecho}
                    data-testid="guardar-valoracion"
                    className="px-4 py-2 rounded-full bg-brand text-white text-sm font-bold
                               disabled:opacity-40 disabled:cursor-default">
                    {hecho ? 'Guardada' : guardando ? 'Guardando…' : 'Guardar'}
                </button>
                {/* Su letra pequeña, tal cual (ficha 17). */}
                <span className="text-xs text-muted-foreground">
                    No es obligatorio. Se puede apagar en Mi perfil.
                </span>
            </div>
        </div>
    );
};

/** «Dejar programada la semana que viene» (ficha 06 del artefacto «Mi semana»).
 *
 *  «El fin de semana se sienta, mira los días que le tocan de entreno y de descanso, y deja
 *  las dietas hechas.» El atajo de su maqueta: copiar la semana anterior y cambiar lo que
 *  sea distinto.
 *
 *  Solo en semanas FUTURAS y sin nada creado: copiar encima de lo que ya montó no es un
 *  atajo, es un riesgo. Los días que no se pueden copiar se dicen, no se callan.
 */
const CopiarLaSemana = ({ semana, api, onCopiada }) => {
    const [copiando, setCopiando] = useState(false);

    const copiar = async () => {
        if (copiando) return;
        setCopiando(true);
        try {
            const anterior = new Date(`${semana.inicio}T12:00:00`);
            anterior.setDate(anterior.getDate() - 7);
            const { data } = await api.post('/diets/semana/copiar', {
                origen: anterior.toLocaleDateString('en-CA'),
                destino: semana.inicio,
            });
            const n = (data.copiados || []).length;
            if (!n) {
                toast.info('No había nada que copiar de la semana anterior.');
            } else {
                const pegas = [];
                if (data.otro_tipo?.length) pegas.push(`${data.otro_tipo.length} con otro tipo de día`);
                if (data.ocupados?.length) pegas.push(`${data.ocupados.length} que ya tenías`);
                toast.success(`${n} ${n === 1 ? 'día copiado' : 'días copiados'}`
                    + (pegas.length ? `. Se quedaron fuera ${pegas.join(' y ')}.` : ''));
            }
            if (onCopiada) onCopiada();
        } catch (e) {
            toast.error('No hemos podido copiar la semana. Inténtalo otra vez.');
        } finally { setCopiando(false); }
    };

    return (
        <div className="surface p-4 mb-4" data-testid="copiar-semana">
            <p className="text-sm text-foreground">La semana que viene, sin crear.</p>
            <p className="text-sm text-muted-foreground mt-0.5">
                Copia la de la semana anterior y cambia lo que sea distinto.
            </p>
            <button type="button" onClick={copiar} disabled={copiando}
                data-testid="boton-copiar-semana"
                className="mt-3 px-4 py-2 rounded-full bg-brand text-white text-sm font-bold disabled:opacity-50">
                {copiando ? 'Copiando…' : 'Copiar la semana anterior'}
            </button>
        </div>
    );
};

/** EL DÍA EN BLOQUES, SIN HORAS (ficha 20 del artefacto «Mi semana», con la decisión del
 *  10-09: «que la semana se dibuje por días y bloques, sin horas»).
 *
 *  La app no guarda ni una hora -- ni de comer, ni de entrenar, ni de dormir --, así que
 *  dibujar una línea de tiempo sería inventárselas. Lo que sí se sabe es el ORDEN, y sale
 *  del cuestionario: cuántas comidas hace y en qué momento entrena.
 *
 *  De un vistazo se ve lo que no se ve en ningún otro sitio: cuándo le cae el entreno
 *  respecto a sus comidas, y si ese día lleva intra y post.
 */
/** LA LÍNEA DE TIEMPO DEL DÍA (ficha 20): «una barra de tiempo por día, de las 6 de la
 *  mañana a las 12 de la noche, con sus cosas colocadas donde van».
 *
 *  El servidor manda cada bloque con su sitio en la barra (`desde` y `hasta`, en tanto por
 *  ciento), así que aquí solo se pintan rectángulos: dónde cae cada cosa se decide en un
 *  sitio y no en dos.
 *
 *  El día sin horas puestas vuelve a la tira de siempre, la del orden sin horas: es mejor
 *  que un hueco, y así el que todavía no ha planificado ve su día igual que antes.
 */
/* LOS COLORES DE SU DIBUJO, uno por uno (12-09):
   «Las rayas naranjas son las comidas; las marrones, el intra y el post; el bloque grande,
    el entreno; y el rayado de los extremos, las horas de sueño.»

   El sueño va RAYADO y no en gris plano: es lo que lo distingue de un bloque de verdad, y
   ademas dice que ahi no hay nada que hacer. El cardio pautado va hueco, con su borde, que
   es como lo dibuja: se ve que ocupa sitio sin competir con el entreno. */
const COLOR_DEL_BLOQUE = {
    comida: 'bg-brand',
    // El intra y el post, en marrón: es el color de su leyenda, y lo que hace es separarlos
    // de las comidas sin sacarlos de la familia.
    peri: 'bg-[#6b5340]',
    entreno: 'bg-brand',
    // El cardio pautado va HUECO, con su borde: ocupa su sitio sin competir con el entreno.
    cardio: 'border border-brand/80',
    extra: 'bg-brand/70',
    sueno: '',
};

const RAYADO_DEL_SUENO = {
    backgroundImage: 'repeating-linear-gradient(45deg,rgba(255,255,255,.08) 0 2px,transparent 2px 6px)',
};

/** LA REGLA DE HORAS, una sola vez encima de los siete días: «6h 10h 14h 18h 22h».
 *  Sin ella la barra son manchas de color sin escala, y la escala es justo lo que hace que
 *  se lea «el jueves entrena a mediodía». */
const LineaDeHoras = () => (
    /* El mismo sangrado que el padding de la tarjeta del día, para que cada hora caiga
       justo encima de su sitio en la barra. Con otro margen la escala miente. */
    <div className="flex mb-1 px-3.5 sm:px-4" data-testid="regla-de-horas" aria-hidden="true">
        {/* HASTA LAS 24, QUE ES DONDE ACABA LA BARRA (Francisco, 12-09: «lo nuestro solo
            llega hasta las 22»). La barra siempre midió de las 6 a las 12 de la noche, pero
            la escala se paraba en el «22h» pegado al borde derecho: el rótulo del final
            nombraba las 22 y el final eran las 24, así que todo lo de la última hora y media
            se leía dos horas antes de donde estaba. De cuatro en cuatro no se puede cerrar
            en 24 con pasos iguales; de tres en tres sí, y encima se lee más fino. */}
        {[6, 9, 12, 15, 18, 21, 24].map((h, i) => (
            <span key={h} className={`text-[10px] text-muted-foreground/70 font-data ${
                i === 0 ? '' : 'flex-1 text-right'}`}>{h}h</span>
        ))}
    </div>
);

const LineaDelDia = ({ linea, sinPlanificar = false, conRejilla = false, pegada = false }) => {
    const bloques = (linea?.bloques || []).filter(b => b.hasta > b.desde);
    return (
        /* LA BARRA, CON SITIO PARA VERSE. Iba a 12 px de alto, como una rayita, y en su
           dibujo es una barra de verdad: lo que se mira aquí es dónde cae cada cosa, y en
           una línea fina los bloques no se distinguen unos de otros. */
        <div className={`${pegada ? '' : 'mt-1.5'} relative h-7 rounded-[3px] overflow-hidden border ${
                sinPlanificar ? 'border-dashed border-border bg-transparent'
                    : 'border-border/60 bg-muted/40'}`}
            data-testid="linea-del-dia"
            style={conRejilla ? LINEAS_DE_LAS_HORAS : undefined}
            title={bloques.map(b => b.etiqueta).join(' · ') || 'Sin planificar'}>
            {bloques.map((b, i) => (
                <span key={`${b.etiqueta}-${i}`} title={b.etiqueta}
                    style={{
                        left: `${b.desde}%`,
                        width: `${Math.max(1.4, b.hasta - b.desde)}%`,
                        ...(b.tipo === 'sueno' ? RAYADO_DEL_SUENO : {}),
                    }}
                    /* SIN REDONDEAR, como en su dibujo (12-09): las marcas van con la
                       esquina viva. Redondeadas, una comida de diez minutos se queda en una
                       pastilla de nada y pierde los dos extremos, que es justo lo que dice
                       a qué hora empieza y acaba. */
                    /* `??` Y NO `||`: el sueño no tiene clase de color -- lo pinta el
                       rayado del `style` --, y con `||` la cadena vacía se leía como que
                       faltaba el color y le ponía el naranja de las comidas. Salía un
                       bloque naranja macizo en cada extremo del día, que es justo lo
                       contrario de lo que tiene que decir. */
                    className={`absolute top-0 bottom-0 ${COLOR_DEL_BLOQUE[b.tipo] ?? 'bg-brand'}`} />
            ))}
        </div>
    );
};

/** LA SEMANA EN EL TIEMPO, EN UN CUADRO PROPIO (Francisco, 12-09).
 *
 *  Son DOS VISTAS y no una, que es lo que su artefacto ya hacía sin decirlo: «El horario de
 *  la semana» es un cuadro para MIRAR y «la semana en curso» una lista para ACTUAR. Metidas
 *  en el mismo sitio se estorban: la barra necesita estar pegada a la de al lado para que
 *  los días se comparen, y la lista necesita aire para el estado, los macros y los botones.
 *
 *  Aquí van los siete juntos, compartiendo escala y con las líneas de las horas de fondo.
 *  Así se lee de un tirón lo que él dice que quiere ver: «que el jueves entrena a mediodía y
 *  las comidas se le juntan; que el sábado entrena por la mañana; y que el domingo no
 *  entrena y come más tarde».
 */
// Cómo se le nombra cada comida en una frase. El peri va con su nombre corto, que es como
// lo llama él en toda la app.
const COMO_SE_LLAMA_LA_COMIDA = (clave) => (
    clave === 'Intra' ? 'el intra' : clave === 'Post' ? 'el post'
        : `la comida ${clave.slice(1)}`
);

// «La planificaste a las 7:30 y la hiciste sobre las 9:00»: la hora de después, sacada de
// la de antes más lo que se le va. Se redondea al cuarto de hora, que es lo que significa
// «sobre las».
// «a las 7:30», no «a las 07:30»: así lo escribe él en la frase, y en una frase el cero
// de delante sobra. En las tablas y en la escala sí se queda, que ahí alinea.
const horaEnFrase = (hhmm) => (hhmm || '').replace(/^0/, '');

// Los días, en letra hasta nueve, que es como se escriben dentro de una frase («cuatro
// días»). De diez en adelante, en cifra.
const DIAS_EN_LETRA = ['cero', 'un', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete',
    'ocho', 'nueve'];
const enLetra = (n) => (n >= 0 && n < DIAS_EN_LETRA.length ? DIAS_EN_LETRA[n] : String(n));

const horaMasMinutos = (hhmm, minutos) => {
    const [h, m] = hhmm.split(':').map(Number);
    let t = h * 60 + m + minutos;
    t = Math.round(t / 15) * 15;
    t = ((t % 1440) + 1440) % 1440;
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};

const LINEAS_DE_LAS_HORAS = {
    backgroundImage: [16.7, 33.3, 50, 66.7, 83.3].map(x =>
        `linear-gradient(to right,transparent ${x}%,rgba(127,127,127,.22) ${x}%,`
        + `rgba(127,127,127,.22) calc(${x}% + 1px),transparent calc(${x}% + 1px))`).join(','),
};

// Qué es cada color, con el orden y las palabras de su leyenda.
const LEYENDA_DEL_TIEMPO = [
    ['bg-brand', 'Comidas'], ['bg-[#6b5340]', 'Intra y post'], ['bg-brand', 'Entreno'],
    ['border border-brand/80', 'Cardio pautado'], ['bg-muted-foreground/25', 'Sueño'],
    ['bg-brand/70', 'Cardio extra'],
];

const RejillaDeLaSemana = ({ semana, onAbrirDia }) => (
    <div className="surface p-4 mb-4" data-testid="rejilla-de-la-semana">
        <p className="caption mb-3">Tu semana en el tiempo</p>
        <LineaDeHoras />
        {/* CON AIRE ENTRE DÍAS (Francisco, 12-09). Estaban a dos píxeles, casi pegados,
            buscando que se leyera como una rejilla; así apelotonado cuesta seguir la fila de
            un día concreto. Con seis, las líneas de las horas siguen atando la vertical y
            cada día se lee solo. */}
        <div className="space-y-1.5">
            {(semana.dias || []).map((dia, i) => (
                <div key={dia.fecha} className="flex items-center gap-2.5"
                    data-testid={`rejilla-${dia.fecha}`}>
                    <span className="w-9 flex-shrink-0 text-right leading-none">
                        <span className={`block text-[9px] font-bold uppercase tracking-wider ${
                            dia.fecha === semana.hoy ? 'text-brand' : 'text-muted-foreground'}`}>
                            {DIAS_CORTOS[i]}
                        </span>
                        <span className={`block text-[13px] font-bold mt-0.5 ${
                            dia.fecha === semana.hoy ? 'text-brand' : 'text-foreground'}`}>
                            {diaDelMes(dia.fecha)}
                        </span>
                    </span>
                    <button type="button" onClick={() => onAbrirDia(dia.fecha)}
                        aria-label={`Ver el ${DIAS_CORTOS[i]} por dentro`}
                        className="flex-1 min-w-0 text-left">
                        <LineaDelDia linea={dia.linea} sinPlanificar={!dia.linea?.puesto}
                            conRejilla pegada />
                    </button>
                    {/* LA CAMPANA CON SU NÚMERO (ficha 07): «que las campanas se vean en la
                        línea de tiempo, para que sepa cuántos tiene ese día sin abrir
                        nada». Es la cuenta de las citas de ese día, las que la app le
                        recordaría: comidas, peri, entreno y el cardio que le pautan. El
                        sueño y los huecos de cardio extra no cuentan, que esos no son una
                        cita. */}
                    <span className={`w-7 flex-shrink-0 flex items-center justify-end gap-0.5
                            text-[10px] font-data ${dia.avisos || dia.linea?.silencio
                                ? 'text-muted-foreground' : 'text-transparent'}`}
                        title={dia.linea?.silencio ? 'Ese día lo pusiste en silencio'
                            : dia.avisos ? `${dia.avisos} avisos ese día` : undefined}
                        data-testid={`avisos-${dia.fecha}`}>
                        {dia.linea?.silencio
                            ? <BellOff className="w-2.5 h-2.5" />
                            : dia.avisos > 0 && <><Bell className="w-2.5 h-2.5" />{dia.avisos}</>}
                    </span>
                </div>
            ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 mt-3">
            {LEYENDA_DEL_TIEMPO.map(([color, que]) => (
                <span key={que} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className={`w-3.5 h-1.5 ${color}`} />
                    {que}
                </span>
            ))}
        </div>
    </div>
);

const TiraDelDia = ({ bloques }) => {
    if (!bloques || bloques.length < 2) return null;
    const COLOR = {
        comida: 'bg-muted-foreground/35',
        peri: 'bg-amber-600/60',
        entreno: 'bg-brand',
        // El cardio pautado, con color propio: es otra cosa que el entreno de pesas y él
        // los separa siempre (ficha 24).
        cardio: 'bg-sky-500/70',
    };
    const COMO = { comida: 'Comida', peri: '', entreno: 'Entreno', cardio: '' };
    return (
        <div className="mt-1.5" data-testid="tira-del-dia"
            title={bloques.map(b => b.etiqueta).join(' · ')}>
            <div className="flex items-center gap-[3px]">
                {bloques.map((b, i) => (
                    <span key={`${b.etiqueta}-${i}`}
                        title={`${COMO[b.tipo] ? `${COMO[b.tipo]} ` : ''}${b.etiqueta}`}
                        className={`h-1.5 rounded-full ${COLOR[b.tipo] || COLOR.comida} ${
                            b.tipo === 'entreno' ? 'flex-[2]' : 'flex-1'}`} />
                ))}
            </div>
            {/* LA SEÑAL DEL MOVIMIENTO SE FUE A LA FILA (12-09), con el ✓ y las estrellas.
                Aquí era una raya azul debajo de la barra: un color que no es de la casa y,
                peor, colgada de una tira que desde la rejilla solo se pinta cuando el
                cliente NO ha puesto sus horas. O sea, el que se organiza el día era justo
                el que no veía si se había movido. */}
        </div>
    );
};

const FILTROS = [
    { id: 'todo', label: 'Todo' },
    { id: 'dieta', label: 'Dieta' },
    { id: 'entreno', label: 'Entreno' },
];

const MiSemanaPage = () => {
    const { api, habilitaciones, profile } = useAuth();
    // ¿SU PLAN LE DA RUTINA? (Trello de César, 24-09: la Calculadora JP y el Mantenimiento
    // sin la rutina comprada veían el filtro «Entreno» y «Entreno / Descanso» en cada día).
    // Es la misma cuenta que CAP.RUTINA pero sin el interruptor de la pantalla de Rutina:
    // apagar esa pantalla no le quita a nadie saber qué días entrena.
    const conEntreno = !habilitaciones || deriveCapabilities(habilitaciones, {
        rutinaVisible: true, rutinaComprada: !!profile?.rutina_comprada })[CAP.RUTINA];
    const filtros = conEntreno ? FILTROS : FILTROS.filter(f => f.id !== 'entreno');
    const navigate = useNavigate();
    const [semana, setSemana] = useState(null);
    // EL LUNES DE LA SEMANA QUE SE ESTÁ MIRANDO (ficha 03 del artefacto «Mi semana», Jesús
    // 6-09-2026). `null` = la de hoy, que es como se entra. Lo mueven las flechas, y de ahí
    // sale el `?inicio=` que el servidor ya sabía atender desde el 21-08: lo único que
    // faltaba era la forma de pedírselo.
    const [inicio, setInicio] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filtro, setFiltro] = useState('todo');
    // ¿Hay algún día con comidas fuera de esta semana? Decide «Repetir» o «Crear» en los
    // días vacíos cuando la semana entera está sin crear (ficha 17). `null` mientras carga.
    const [hayRecientes, setHayRecientes] = useState(null);
    // El objetivo de cada día creado, para decir por cuánto se sale (ficha 124).
    const [objetivos, setObjetivos] = useState({});

    // Se vuelve a pedir la semana entera cuando el cliente contesta qué días entrena: los
    // siete días cambian de golpe (ficha 06), así que no vale con retocar una fila.
    const [recarga, setRecarga] = useState(0);

    useEffect(() => {
        // Con el «hoy» del RELOJ DEL CLIENTE (bloque F, 23-08): la pastilla «Hoy» y la
        // semana por defecto son suyas, no las de España.
        const hoyCliente = new Date().toLocaleDateString('en-CA');
        setLoading(true);
        api.get(`/diets/semana?hoy_cliente=${hoyCliente}${inicio ? `&inicio=${inicio}` : ''}`)
            .then(r => setSemana(r.data))
            .catch(e => {
                console.error('Error cargando la semana:', e);
                toast.error('No hemos podido cargar tu semana. Inténtalo en un momento.');
            })
            .finally(() => setLoading(false));
        // «REPETIR» SOLO SI HAY ALGO QUE REPETIR (ficha 17 del parte de Gonzalo, 5-09). El
        // rótulo salía en todos los días vacíos, también en la cuenta recién abierta que no
        // tiene ni un día con comidas: prometía repetir la nada. El servidor ya devuelve
        // solo días con alimentos, de hoy hacia atrás; con uno basta.
        api.get(`/diets/recent?limit=1&hoy_cliente=${hoyCliente}`)
            .then(r => setHayRecientes((r.data?.diets || []).length > 0))
            .catch(() => setHayRecientes(false));
    }, [api, recarga, inicio]);

    // EL DESVÍO EN VEZ DE LOS TRES TOTALES (ficha 124 del parte de Gonzalo, 5-09).
    //
    // «Creada · 197 · 164 · 68» obliga a restar de cabeza contra un objetivo que no está en
    // la pantalla; «Sin cuadrar · −101 H» dice lo que hay que arreglar. El objetivo es el
    // mismo que enseña la cabecera de Nutrición para ese día (`/calculator/distribute`, con
    // la configuración guardada del día: tipo, comidas y perientreno), porque los totales
    // de la fila ya se cuentan como allí, con el intra y el post dentro. Solo se pide para
    // los días creados; mientras llega o si falla, la fila enseña los tres totales.
    // EL OBJETIVO LO MANDA LA SEMANA (10-09, decisión 6 de Jesús del 8-09).
    //
    // Aquí había DOS viajes por cada día creado -- la dieta y el reparto -- para saber
    // contra qué medir el desvío, o sea hasta catorce peticiones para pintar siete filas.
    // Y lo peor no era el gasto: el número que salía de ahí NO ERA EL MISMO con el que el
    // servidor decide si el día cuadra. Medido en la cuenta de demostración, el servidor
    // juzgaba contra 170 · 140 · 50 y esta pantalla restaba contra 210 · 170 · 50, y por
    // eso había filas que decían «Sin cuadrar» sin ningún desvío que enseñar, y entonces
    // caían en la rama de las cifras. Los dos formatos en la misma semana que él denuncia
    // salían de aquí.
    //
    // Ahora `/diets/semana` trae `objetivo` en cada día, calculado por el mismo juez que
    // pone el `is_cuadrado`. Una sola fuente, que es su regla.
    useEffect(() => {
        const dias = semana?.dias || [];
        if (!dias.length) return;
        setObjetivos(Object.fromEntries(dias.map(d => [d.fecha, d.objetivo || null])));
    }, [semana]);

    // A DÓNDE LLEVA LA FILA DE UN DÍA (Jesús, 13-09, con su vídeo delante).
    //
    //     «Si pincha en entreno lo lleva a la pestaña de Nutrición.»
    //
    // No era ningún fallo del iPhone: en el filtro ENTRENO la fila lleva el rótulo «Ver
    // día» y la fila entera abría NUTRICIÓN. El cliente está mirando sus entrenos, toca uno
    // y aterriza en su dieta. En el vídeo se ve entero: filtra por Entreno, toca la primera
    // fila (MAR 8) y sale «Nutrición · martes 8 de septiembre».
    //
    // Con ese filtro la fila va al DÍA POR DENTRO, que es lo que dice su rótulo y lo que ha
    // venido a ver: la hora del entreno, el grupo que le toca y su cardio. En «Todo» y en
    // «Dieta» se queda como estaba, que allí lo que mira es la dieta.
    // EL BOTÓN DE UN HITO QUE APUNTA A ESTA MISMA PANTALLA (Francisco, 13-09: «Valorarla no
    // hace nada»). Y no hacía nada literalmente: el hito «Cómo fue tu semana» lleva a
    // `/dashboard/semana`, que es donde ya estás, así que el `navigate` no movía ni un
    // píxel. La valoración está en esa misma página, más abajo, y lo que hace falta es
    // llevar al cliente HASTA ella.
    const irAlHito = (link) => {
        const [ruta] = (link || '').split('?');
        if (ruta !== '/dashboard/semana') { navigate(link); return; }
        const bloque = document.querySelector('[data-testid="valoracion-semana"]');
        if (!bloque) { navigate(link); return; }
        bloque.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Un parpadeo del borde, que si no el que lo pulsa no sabe si ha pasado algo.
        bloque.classList.add('ring-2', 'ring-brand');
        setTimeout(() => bloque.classList.remove('ring-2', 'ring-brand'), 1600);
    };

    const irAlDia = (fecha) => navigate(
        filtro === 'entreno' ? `/dashboard/semana/dia/${fecha}`
            : `/dashboard/nutrition?date=${fecha}`);

    // Mover la semana: se le suma o resta una semana al lunes que se está mirando, y el
    // servidor devuelve la que toque. Se pide en días y no en semanas porque el que manda
    // es el `?inicio=`, que es una fecha cualquiera de esa semana.
    const moverSemana = (dias) => {
        const base = semana?.inicio;
        if (!base) return;
        const d = new Date(`${base}T12:00:00`);
        d.setDate(d.getDate() + dias);
        setInicio(d.toLocaleDateString('en-CA'));
    };

    if (loading) {
        return <Wrap><div className="animate-pulse space-y-3 max-w-2xl">
            <div className="h-9 bg-muted rounded w-1/3" />
            <div className="h-5 bg-muted rounded w-2/3" />
            <div className="h-10 bg-muted rounded-xl w-56" />
            {[...Array(7)].map((_, i) => <div key={i} className="h-20 bg-muted rounded-2xl" />)}
        </div></Wrap>;
    }

    if (!semana) {
        return <Wrap>
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-foreground mb-6" data-testid="mi-semana-heading">Mi semana</h1>
            <div className="surface p-10 text-center max-w-2xl">
                <p className="text-muted-foreground text-sm">No hemos podido cargar tu semana. Recarga la página para intentarlo otra vez.</p>
            </div>
        </Wrap>;
    }

    const r = semana.resumen || {};
    // LOS ENTRENOS SE FUERON DE LA CABECERA (ficha 09): «lo demás ya se ve día a día», y
    // el entreno de cada día está en su fila, con su grupo y con su «hecho» cuando el
    // cliente marca sus sesiones. En la semana cerrada, además, el «Cómo fue» los cuenta
    // en pasado («2 de 6 entrenos hechos»), que es donde ese número sí dice algo.
    // EL TITULAR CUENTA LAS QUE CUADRAN, no solo las creadas (revisión del 2-09):
    // «7 creadas» con las siete sin cuadrar es el mismo problema que el verde de las filas.
    // Se cuenta aquí, sobre los días que ya llegan, para no pedirle otro número al servidor.
    const cuadradas = (semana.dias || []).filter(d => d.estado === 'montada' && d.is_cuadrado).length;
    const creadas = r.montadas || 0;
    // LO QUE LE QUEDA POR HACER, EN UNA FRASE (ficha 09 del artefacto «Mi semana», Jesús
    // 6-09-2026): «Te queda 1 día por crear y 4 por cuadrar. Lo demás ya se ve día a día».
    //
    // Aquí ponía cuatro cifras seguidas -- «6 creadas (2 cuadradas) · 0 empezadas · 1 sin
    // crear · 5 entrenos» -- que hay que leer despacio para sacar lo único que se puede
    // hacer algo con ello. Y una de ellas no la entendía nadie: «0 empezadas», que además
    // es un cero, o sea una cuenta de lo que NO ha pasado.
    //
    // SOLO SALE LO QUE NO ES CERO, que es lo que quita el «0 empezadas» sin tener que
    // nombrarlo: si no hay ninguno a medias, ese trozo no existe. Y son tres cosas y no
    // dos porque en la app son tres acciones distintas, cada una con su rótulo en la fila:
    // crear el día, terminarlo y cuadrarlo.
    // TERMINAR Y CUADRAR NO SE MEZCLAN (Jesús, 11-09, punto 15).
    //
    // `porCuadrar` era «las creadas menos las cuadradas», y ahí entraba TODO lo que no
    // estaba perfecto: también los días que simplemente se quedan CORTOS. Resultado: el
    // mismo sábado salía «sin terminar» en su fila y en Inicio, y arriba se contaba como
    // «6 por cuadrar». Sus dos definiciones, que son las que mandan:
    //
    //   Terminar — al día le falta comida: no llega a los macros. Añade el cliente.
    //   Cuadrar  — el día está hecho, pero contra unos macros que ya no son los suyos.
    //              Se pasa, y ahí reajusta la app.
    //
    // Así que se mira el desvío de cada día con la misma vara que su propia fila: si algún
    // macro SE PASA, es de cuadrar; si sólo va corto, es de terminar.
    //
    // OJO, ESTO CAMBIA LO QUE ÉL MISMO PIDIÓ EL 6-09 («1 día por crear y 4 por cuadrar»):
    // manda lo más reciente, y lo más reciente es que esas dos palabras no se mezclan.
    // Con el mismo objetivo que usa su fila (`objetivos[fecha]`), que es la decisión 6 del
    // 8-09: una sola vara. Si todavía no ha llegado, el día cuenta como «por terminar», que
    // es lo que él dice: ir corto no es un error, es no haber acabado.
    const seP = (d) => desviosDelDia(d.macros || {}, objetivos[d.fecha]).some(x => x.d > 0);
    const creadasSinCuadrar = (semana.dias || [])
        .filter(d => d.estado === 'montada' && !d.is_cuadrado);
    const porCrear = r.sin_montar || 0;
    const porTerminar = (r.empezadas || 0) + creadasSinCuadrar.filter(d => !seP(d)).length;
    const porCuadrar = creadasSinCuadrar.filter(seP).length;
    const tramos = [
        [porCrear, `${plural(porCrear, 'día')} por crear`],
        [porTerminar, `${porTerminar} por terminar`],
        [porCuadrar, `${porCuadrar} por cuadrar`],
    ].filter(([n]) => n);
    const pendientes = tramos.map(([, t]) => t);
    // «1 día por crear y 4 por cuadrar»: la unidad se dice una vez, en el primer tramo.
    const listaPendientes = pendientes.length > 1
        ? `${pendientes.slice(0, -1).join(', ')} y ${pendientes[pendientes.length - 1]}`
        : pendientes[0];
    // EN LA SEMANA CERRADA NO SE DICE: «te queda» es una tarea, y una semana que ya pasó no
    // deja tareas. Ahí lo que va arriba es el «Cómo fue» (ficha 04), que ya cuenta lo mismo
    // en pasado y sin pedir nada.
    const contador = semana.cerrada
        ? null
        : pendientes.length
            /* «Te queda 1 día», no «Te quedan 1 día» (Francisco, 11-09, punto 17). Miraba
               solo los días por crear, así que con «1 por terminar» a secas volvía a decir
               «quedan». Lo que manda es el número del PRIMER tramo, que es el que va
               pegado al verbo, y solo cuando no hay más de uno. */
            ? `Te ${tramos.length === 1 && tramos[0][0] === 1 ? 'queda' : 'quedan'} ${listaPendientes}`
            : 'Toda la semana creada y cuadrada';

    // Algo que repetir: un día de esta semana con comidas, o cualquiera de antes.
    const hayQueRepetir = (semana.dias || []).some(d => d.estado !== 'sin_montar') || hayRecientes === true;

    // LA PREGUNTA QUE FALTABA (ficha 06 del doc de Jesús del 5-09). El día sin dieta decía
    // «Por decidir» y ahí se quedaba: el tipo del día solo se elegía al crearlo, con el
    // interruptor de Nutrición, así que la app no sabía de antemano si un lunes era de
    // entreno. Ahora, cuando queda alguno por decidir, se le pregunta AQUÍ MISMO, que es
    // donde lo está echando en falta, y con el mismo trozo de pantalla que Mi perfil.
    //
    // «Queda alguno por decidir» es la única condición, y con eso basta: al que tiene rutina
    // que cubre la semana entera no le queda ninguno y no se le pregunta, y al que la tiene
    // a medias se le pregunta por los días que su rutina no dice, que es lo que hace falta.
    const porDecidir = (semana.dias || []).filter(d => !d.tipo_dia).length;
    const susDias = semana.dias_de_entreno || {};

    return (
        <Wrap>
            {/* `mx-auto`: tenía el ancho máximo pero no el margen, así que en pantalla
                grande todo se quedaba pegado al borde izquierdo con medio metro de hueco a
                la derecha. */}
            <div className="max-w-2xl mx-auto">
                {/* Cabecera */}
                <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-foreground leading-none mb-2" data-testid="mi-semana-heading">Mi semana</h1>

                {/* EN QUÉ SEMANA ESTÁ (ficha 07): «la semana de membresía no es la semana de
                    rutina», y esta es la pantalla donde toca decirlo. Va debajo del título,
                    donde él lo pide, y en gris: es dónde está, no una alerta. */}
                {enQueSemanaEsta(semana) && (
                    <p className="text-sm text-muted-foreground mb-3" data-testid="mi-semana-en-que-semana">
                        {enQueSemanaEsta(semana)}
                    </p>
                )}

                {/* EL LEMA DE LA PANTALLA (su maqueta del horario de la semana): «Si no te
                    planificas, te estás planificando para fallar. Marca tus horarios y verás
                    que te da tiempo a todo.»

                    Es lo que explica para qué sirve esto, y por eso va arriba y no escondido:
                    la pantalla dejó de ser un resumen de lo que pasó para ser donde se
                    planifica. «Marca tus horarios» lleva a ponerlos. */}
                <p className="text-sm text-muted-foreground mb-3" data-testid="mi-semana-lema">
                    Si no te planificas, te estás planificando para fallar.{' '}
                    <button type="button" onClick={() => navigate('/dashboard/semana/horario')}
                        className="text-foreground font-semibold hover:underline underline-offset-4">
                        Marca tus horarios
                    </button>{' '}
                    y verás que te da tiempo a todo.
                </p>

                {/* LO QUE LE QUEDA POR HACER, ARRIBA Y EN SU CAJA (su maqueta de «La semana
                    en curso»): va justo debajo de la semana del ciclo, antes de las flechas,
                    con el borde naranja. Estaba suelto y más abajo, entre los hitos y los
                    filtros, donde se lee como un pie de foto en vez de como el titular. */}
                {contador && (
                    <div className="rounded-xl border border-brand/40 bg-brand/[0.06] px-4 py-2.5 mb-3"
                        data-testid="mi-semana-contador">
                        <p className="text-sm text-foreground">{contador}</p>
                    </div>
                )}

                {/* MOVERSE ENTRE SEMANAS (ficha 03 del artefacto «Mi semana», Jesús
                    6-09-2026): «una flecha a cada lado y la fecha en medio». Es lo primero
                    que se espera de una pantalla que se llama así, y hasta hoy no existía:
                    el lunes, cuando la semana se vaciaba, no había forma de mirar la
                    anterior.

                    Los límites los dice el servidor y no se adivinan aquí: atrás, hasta el
                    día que entró; adelante, hasta el final de la quincena, que es «el tramo
                    con el que trabajáis». Una flecha que no lleva a ningún sitio se apaga en
                    vez de desaparecer: así el sitio no se mueve al llegar al tope. */}
                <div className="flex items-center gap-2 mb-3" data-testid="mi-semana-navegador">
                    <button type="button" onClick={() => moverSemana(-7)}
                        disabled={!semana.puede_atras} aria-label="La semana anterior"
                        data-testid="semana-atras"
                        className="w-9 h-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/50 transition-colors disabled:opacity-30 disabled:hover:text-muted-foreground disabled:hover:border-border">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <p className="flex-1 text-center text-sm font-semibold text-foreground"
                        data-testid="semana-rango">
                        {tramoDeLaSemana(semana.inicio, semana.fin)}
                    </p>
                    <button type="button" onClick={() => moverSemana(7)}
                        disabled={!semana.puede_adelante} aria-label="La semana siguiente"
                        data-testid="semana-adelante"
                        className="w-9 h-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/50 transition-colors disabled:opacity-30 disabled:hover:text-muted-foreground disabled:hover:border-border">
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>

                {/* «TODAVÍA NO HAS PUESTO TUS HORARIOS» (artefacto, «La primera vez»).
                    Mientras no los tenga, la semana no se puede dibujar en el tiempo: es lo
                    primero que hay que ofrecerle, y con sus palabras. En cuanto pone uno
                    desaparece y queda el enlace pequeño de abajo. */}
                {semana.horarios_puestos === false && (
                    <button type="button" onClick={() => navigate('/dashboard/semana/horario')}
                        data-testid="semana-sin-horarios"
                        className="w-full surface surface-hover p-4 mb-4 flex items-center justify-between gap-3 text-left border border-brand/40 bg-brand/[0.06]">
                        <span className="min-w-0">
                            <span className="block text-sm font-bold text-foreground">
                                Todavía no has puesto tus horarios
                            </span>
                            <span className="block text-xs text-muted-foreground mt-0.5">
                                Ponles la hora a tus comidas y te queda la semana montada.
                            </span>
                        </span>
                        <ChevronRight className="w-5 h-5 text-brand flex-shrink-0" />
                    </button>
                )}

                {/* Y UNA SALIDA PARA VOLVER, que si no se pierde: con tres semanas atrás, la
                    única forma de volver a la de hoy era pulsar la flecha tres veces. */}
                {!semana.es_la_de_hoy && (
                    <button type="button" onClick={() => setInicio(null)} data-testid="semana-volver-a-hoy"
                        className="text-sm font-semibold text-brand hover:underline underline-offset-4 mb-3">
                        Volver a esta semana
                    </button>
                )}

                {/* LOS HITOS DE LA SEMANA (fichas 05 y 23 del artefacto «Mi semana», Jesús
                    6-09-2026): «un bloque arriba con lo que hay... eso son tus propias
                    reglas puestas en pantalla».

                    EN ESQUEMA, que es como él lo pide en la 23: el día a la izquierda y el
                    hito a la derecha, sin charla. Y cada uno con su salida (ficha 13), que
                    un hito que no lleva a ningún sitio obliga a buscar la pantalla a mano.

                    SOLO LOS TRES QUE LA APP SABE DE VERDAD -- el reporte, su fecha límite
                    y el pesaje --, que salen del mismo calendario que los avisos. El
                    cambio de rutina, el programa nuevo y las ofertas de Full Gas no
                    existen como fecha en ninguna parte, y prometerlos en pantalla sería
                    peor que no decirlos.

                    En la semana que no trae ninguno el bloque no sale: dos de cada cuatro
                    semanas no tienen hito, y un bloque vacío es ruido. */}
                {(semana.hitos || []).length > 0 && (
                    <div className="surface p-4 mb-4" data-testid="mi-semana-hitos">
                        <p className="caption mb-3">Hitos de la semana</p>

                        {/* LA SEMANA DE UN VISTAZO (su maqueta: «L M💪 X⚖ J🏷 V📄 S D L⏰»).
                            Siete cuadros, uno por día, y el que trae hito se enciende con su
                            icono debajo. Antes de leer una sola línea ya se ve en qué días
                            pasa algo, que es para lo que sirve una semana dibujada. */}
                        <div className="flex gap-1.5 mb-3" data-testid="tira-de-hitos">
                            {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((letra, i) => {
                                const suyos = (semana.hitos || []).filter(
                                    h => diaCortoDe(h.dia) === DIAS_CORTOS[i]);
                                const hay = suyos.length > 0;
                                return (
                                    <div key={i} data-testid={`tira-hito-${i}`}
                                        title={suyos.map(h => h.titulo).join(' · ') || undefined}
                                        className={`flex-1 rounded-lg py-2 text-center border ${
                                            hay ? 'border-brand/45 bg-brand/[0.09]' : 'border-border bg-muted/30'}`}>
                                        {/* SIN ICONOS (Francisco, 12-09). Los emojis de los
                                            hitos daban ruido y no decían nada que no diga ya
                                            el día encendido: lo que se busca aquí es en qué
                                            días pasa algo, y eso lo dice el color. */}
                                        <p className={`text-xs font-bold ${
                                            hay ? 'text-brand' : 'text-muted-foreground'}`}>{letra}</p>
                                    </div>
                                );
                            })}
                        </div>
                        <ul className="space-y-2.5">
                            {semana.hitos.map((h, i) => (
                                <li key={`${h.dia}-${i}`} className="flex flex-wrap items-start gap-3 text-sm"
                                    data-testid={`hito-${h.dia}`}>
                                    <span className="w-11 flex-shrink-0 text-xs font-bold uppercase tracking-wider text-brand pt-0.5">
                                        {diaCortoDe(h.dia)}
                                    </span>
                                    <span className="flex-1 min-w-0">
                                        <span className="text-foreground font-semibold">{h.titulo}</span>
                                        {h.hora && <span className="text-muted-foreground"> · {h.hora}</span>}
                                        {h.detalle && (
                                            <span className="block text-xs text-muted-foreground mt-0.5">{h.detalle}</span>
                                        )}
                                    </span>
                                    {/* EL PESAJE, CON EL CAMPO AHÍ MISMO (ficha 13): «el
                                        pesaje, con el campo de kilos ahí mismo, sin salir de
                                        la pantalla». Los demás hitos llevan su rótulo, que
                                        lleva a donde se hacen; este se hace aquí, en dos
                                        toques, y por eso es el único que trae el campo.

                                        Es el mismo `CampoDePeso` de Evolución, que es «el
                                        único sitio donde el peso se escribe»: se reusa
                                        entero para no abrir una segunda puerta que guarde
                                        el peso de otra manera. */}
                                    {h.tipo === 'peso' ? (
                                        <div className="w-full sm:w-auto sm:min-w-[210px] flex-shrink-0"
                                            data-testid={`hito-peso-${h.dia}`}>
                                            <CampoDePeso api={api} onGuardado={() => setRecarga(n => n + 1)}
                                                conLosDosNumeros={false} />
                                        </div>
                                    ) : h.link && h.accion && (
                                        <button type="button" onClick={() => irAlHito(h.link)}
                                            data-testid={`hito-accion-${h.dia}`}
                                            className="flex-shrink-0 text-xs font-bold uppercase tracking-wider text-brand hover:underline underline-offset-4">
                                            {h.accion}
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* EL RESUMEN DE LA SEMANA QUE PASÓ (fichas 04 y 15). Solo en las cerradas:
                    en la semana en curso, «2 de 7 días cerrados» un miércoles no dice que
                    vaya mal, dice que aún no ha llegado el jueves.

                    Es la única pantalla donde el cliente ve su semana entera de un vistazo:
                    hoy, para saber si cumplió, tiene que abrir los siete días uno a uno. */}
                {/* Y EL DOMINGO TAMBIÉN, que es el día en que más falta hace (12-09).
                    Esto pedía `semana.cerrada`, y el domingo la semana todavía no lo está:
                    el cliente abría el día en que se le pide valorar la semana y no veía ni
                    uno de sus números, solo la caja de las estrellas. Se le pedía nota de
                    algo que no podía mirar. El servidor ya manda el resumen ese día -- lo
                    decide `es_el_ultimo_dia`, con esta misma ficha escrita al lado --, así
                    que basta con pintar lo que llega y no volver a decidirlo aquí. */}
                {semana.cierre && (
                    <div className="surface p-4 mb-4" data-testid="mi-semana-cierre">
                        <p className="caption mb-3">
                            {semana.es_la_de_hoy ? 'Cómo fue tu semana' : 'Cómo fue'}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            {cifrasDelCierre(semana.cierre).map(c => (
                                <div key={c.que} className="rounded-xl bg-muted/40 px-3 py-2.5"
                                    data-testid={`cierre-${c.id}`}>
                                    <p className="font-data text-lg font-bold text-foreground leading-none">{c.valor}</p>
                                    <p className="text-xs text-muted-foreground mt-1">{c.que}</p>
                                </div>
                            ))}
                        </div>
                        {/* La línea de lo que movió, y debajo la de la semana anterior
                            (ficha 15): «sin comparar, el histórico es siete pantallas
                            sueltas». Sin gráfica ni nada más, que es lo que él pide. */}
                        {loQueMovio(semana.cierre) && (
                            <p className="text-sm text-foreground mt-3" data-testid="cierre-movio">
                                {loQueMovio(semana.cierre)}
                            </p>
                        )}
                        {semana.cierre.kg_anterior != null && (
                            <p className="text-sm text-muted-foreground" data-testid="cierre-anterior">
                                La anterior: {textoKg(semana.cierre.kg_anterior)}
                            </p>
                        )}
                        {/* LA MEDIA DE SUS ESTRELLAS (ficha 12 del artefacto «Mi semana»):
                            «la media de las estrellas que ya puso cada día». Sale de lo que
                            contestó al cerrar cada día, así que aquí no se le pregunta nada.

                            Se dice de cuántos días es: la media de dos días no es la de la
                            semana, y sin el número parecería que valoró los siete. */}
                        {/* LO QUE PLANIFICÓ CONTRA LO QUE HIZO (ficha 28): «dos columnas
                            y ya está: Plan y Hecho. En verde lo que cumplió, en ámbar lo
                            que no. Sin rojo y sin riña, que es tu regla».

                            Sin las filas de horas -- «comidas a su hora» y «horas de
                            sueño» --, que la app no guarda ninguna: medir el cumplimiento
                            de una hora que nadie ha apuntado sería inventarse el dato. */}
                        {(semana.cierre.plan_contra_hecho || []).length > 0 && (
                            <div className="mt-4" data-testid="plan-contra-hecho">
                                <div className="flex items-center justify-end gap-6 text-[11px] font-bold
                                                uppercase tracking-wider text-foreground/40 mb-1">
                                    <span className="w-14 text-right">Plan</span>
                                    <span className="w-14 text-right">Hecho</span>
                                </div>
                                {semana.cierre.plan_contra_hecho.map(f => (
                                    <div key={f.que} className="flex items-center justify-between gap-3 py-1.5
                                                                border-t border-border"
                                        data-testid={`plan-${f.que.toLowerCase().replace(/ /g, '-')}`}>
                                        <span className="text-sm text-foreground min-w-0">{f.que}</span>
                                        <span className="flex items-center gap-6 flex-shrink-0">
                                            <span className="w-14 text-right text-sm text-muted-foreground font-data">
                                                {f.plan}
                                            </span>
                                            {/* Verde si llegó, ámbar si no. Nunca rojo.
                                                Las horas («52 h 30») no se pueden comparar
                                                como números, así que esa fila trae dicho
                                                si cumple. */}
                                            <span className={`w-14 text-right text-sm font-data ${
                                                (f.cumple ?? f.hecho >= f.plan)
                                                    ? 'text-emerald-400' : 'text-amber-400'}`}>
                                                {f.hecho}
                                            </span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* EL MOVIMIENTO DE LA SEMANA (ficha 26): «Te moviste más de lo
                            normal 3 de 7 días». Solo si contestó algún día: sin respuestas
                            no hay nada que contar. */}
                        {/* La línea suelta «Te moviste más de lo normal 2 de 3 días» se fue
                            (12-09): la tabla de arriba ya trae esa cuenta con el nombre de
                            su maqueta, «Cardio extra», y decirlo dos veces en la misma
                            pantalla era leer dos números distintos de lo mismo. */}
                        {/* «LO QUE MÁS SE TE HA MOVIDO» (ficha 08). Es la frase que él
                            llama la más valiosa de la pantalla, y hasta hoy no se podía
                            escribir porque nadie apuntaba a qué hora comía: ahora la
                            casilla del Inicio guarda la hora en que se marca cada comida.
                            No riñe: dice lo que pasó, para que la semana que viene el plan
                            se parezca más a la vida. */}
                        {semana.cierre.la_que_mas_se_mueve?.hora_plan && (
                            <p className="text-sm text-muted-foreground" data-testid="cierre-lo-que-mas-se-mueve">
                                Lo que más se te ha movido: {COMO_SE_LLAMA_LA_COMIDA(semana.cierre.la_que_mas_se_mueve.comida)}.
                                {' '}La planificaste a las {horaEnFrase(semana.cierre.la_que_mas_se_mueve.hora_plan)} y la hiciste
                                {' '}sobre las {horaEnFrase(horaMasMinutos(semana.cierre.la_que_mas_se_mueve.hora_plan,
                                    semana.cierre.la_que_mas_se_mueve.minutos))}
                                {' '}{enLetra(semana.cierre.la_que_mas_se_mueve.dias)}
                                {semana.cierre.la_que_mas_se_mueve.dias === 1 ? ' día' : ' días'}.
                            </p>
                        )}
                        {/* «LA MEDIA DE TUS DÍAS: ★★★★☆» (su maqueta del domingo). Vuelve
                            con las estrellas: el servidor la mandaba desde el principio y
                            la pantalla no la pintaba, así que era un dato servido y no
                            enseñado. Se redondea a la estrella entera, que es como está
                            dibujada, y el número exacto va al lado por si la media cae
                            entre dos. */}
                        {semana.cierre.estrellas_dias > 0 && semana.cierre.estrellas_media != null && (
                            <p className="text-sm text-muted-foreground" data-testid="cierre-media-estrellas">
                                La media de tus días:{' '}
                                <span className="text-brand">
                                    {'★'.repeat(Math.round(semana.cierre.estrellas_media))}
                                    {'☆'.repeat(5 - Math.round(semana.cierre.estrellas_media))}
                                </span>
                                {' '}({num1(semana.cierre.estrellas_media)} de 5, en{' '}
                                {semana.cierre.estrellas_dias}
                                {semana.cierre.estrellas_dias === 1 ? ' día' : ' días'})
                            </p>
                        )}
                    </div>
                )}

                {/* El atajo para dejarla programada (ficha 06): en una semana que todavía
                    no ha llegado y a la que le queda algún día por crear. No se exige que
                    esté entera vacía -- con un día ya empezado el atajo sigue valiendo para
                    los otros seis --, y lo que ya tiene comida no se pisa: eso lo sostiene
                    el servidor, que se salta los días ocupados y los cuenta aparte. */}
                {semana.hoy && semana.inicio > semana.hoy && semana.resumen?.sin_montar > 0 && (
                    <CopiarLaSemana semana={semana} api={api}
                        onCopiada={() => setRecarga(n => n + 1)} />
                )}

                {/* LOS HITOS DE LA SEMANA QUE VIENE, EL DOMINGO (ficha 12): «con lo que le
                    espera y lo que tiene que hacer antes». Va debajo de la valoración y
                    encima del botón de programarla, que es el orden de su maqueta. */}
                {(semana.hitos_siguiente || []).length > 0 && (
                    <div className="surface p-4 mb-4 border border-brand/40"
                        data-testid="hitos-semana-que-viene">
                        <p className="caption mb-3" style={{ color: 'var(--brand, #FF671F)' }}>
                            Hitos de la semana que viene
                        </p>
                        <ul className="space-y-2.5">
                            {semana.hitos_siguiente.map((h, i) => (
                                <li key={`${h.dia}-${i}`} className="flex items-start gap-3 text-sm">
                                    <span className="w-11 flex-shrink-0 text-xs font-bold uppercase tracking-wider text-brand pt-0.5">
                                        {diaCortoDe(h.dia)}
                                    </span>
                                    <span className="flex-1 min-w-0">
                                        <span className="text-foreground font-semibold">{h.titulo}</span>
                                        {h.detalle && (
                                            <span className="block text-xs text-muted-foreground mt-0.5">{h.detalle}</span>
                                        )}
                                    </span>
                                    {h.link && h.accion && (
                                        <button type="button" onClick={() => irAlHito(h.link)}
                                            className="flex-shrink-0 text-xs font-bold uppercase tracking-wider text-brand hover:underline underline-offset-4">
                                            {h.accion}
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Y LA SALIDA A PROGRAMAR LA QUE VIENE (ficha 12): en su maqueta del
                    domingo, debajo de la valoración, «Programar la semana que viene». Sin
                    esto el domingo se acaba en la estrella y el atajo para dejar montada la
                    siguiente solo aparece si al cliente se le ocurre pulsar la flecha.
                    Lleva a la semana de al lado, que es donde está el copiar. */}
                {semana.es_la_de_hoy && semana.puede_adelante && semana.se_puede_valorar && (
                    <button type="button" onClick={() => moverSemana(7)}
                        data-testid="programar-la-que-viene"
                        className="w-full surface surface-hover p-4 mb-4 flex items-center justify-between gap-3 text-left">
                        <span className="min-w-0">
                            <span className="block text-sm font-bold text-foreground">
                                Programar la semana que viene
                            </span>
                            <span className="block text-xs text-muted-foreground mt-0.5">
                                Deja los días hechos y llegas el lunes con todo puesto.
                            </span>
                        </span>
                        <ChevronRight className="w-5 h-5 text-brand flex-shrink-0" />
                    </button>
                )}

                {/* La valoración: el domingo y en las semanas ya pasadas (ficha 12). */}
                {semana.se_puede_valorar && (
                    <ValoracionDeLaSemana hayResumenArriba={Boolean(semana.cierre)} semana={semana} api={api}
                        onGuardada={() => setRecarga(n => n + 1)} />
                )}

                {/* LA PUERTA AL HORARIO, ARRIBA Y CON NOMBRE (12-09). Estaba de enlace
                    pequeño al pie de la leyenda de colores, y ahí no lo encontraba nadie:
                    el que ya tiene sus horas puestas no ve la tarjeta de arriba, así que ese
                    enlace era la única entrada. Aquí va al lado de los filtros, que es donde
                    se mira, y dice qué hay detrás en vez de solo su nombre. */}
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-5">
                    <div className="inline-flex bg-muted rounded-xl p-1 gap-1" role="tablist" data-testid="mi-semana-filtro">
                    {filtros.map(f => (
                        <button key={f.id} role="tab" aria-selected={filtro === f.id}
                            onClick={() => setFiltro(f.id)} data-testid={`filtro-${f.id}`}
                            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors
                                ${filtro === f.id ? 'bg-brand text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                            {f.label}
                        </button>
                    ))}
                    </div>
                    <button type="button" onClick={() => navigate('/dashboard/semana/horario')}
                        data-testid="ir-a-mi-horario"
                        className="flex-shrink-0 whitespace-nowrap text-xs font-bold uppercase tracking-wider text-brand hover:underline underline-offset-4">
                        Horarios
                    </button>
                </div>

                {/* ¿Qué días entrenas? MIENTRAS NO LO HAYA CONTESTADO, aunque no quede ningún
                    «Por decidir» (Francisco, 5-09). Salía solo con días por decidir, y en
                    cuanto se aplicó aquí la regla del fin de semana no quedó ninguno: la
                    pregunta desapareció y con ella la mitad de la ficha 06, que es
                    justamente que se le pregunte. La semana se pinta con lo que suponemos;
                    esto es para que deje de ser una suposición. En cuanto marca sus días, se
                    va sola. Si tiene rutina puesta tampoco se pregunta: manda la rutina. */}
                {!susDias.marcados && !semana.tiene_rutina && (
                    <div className="surface p-4 mb-5" data-testid="mi-semana-pregunta-dias">
                        <p className="text-sm font-semibold text-foreground mb-1">
                            ¿Qué días entrenas?
                        </p>
                        <p className="text-sm text-muted-foreground mb-3">
                            {porDecidir > 0
                                // «TE QUEDA 1 DÍA», NO «TE QUEDAN 1 DÍA» (Jesús, 11-09,
                                // punto 17). El verbo también concuerda, no solo el nombre.
                                ? `Te ${porDecidir === 1 ? 'queda' : 'quedan'} ${plural(porDecidir, 'día')} por decidir. ${porDecidir === 1 ? 'Márcalo' : 'Márcalos'} y lo sabremos de antemano.`
                                : 'Mientras no nos lo digas, damos por hecho que descansas el fin de semana. Márcalos y lo sabremos de verdad.'}
                        </p>
                        <DiasQueEntrenas marcados={susDias.marcados} compacto
                            onGuardado={() => setRecarga(n => n + 1)} />
                    </div>
                )}

                {/* LA SEMANA EN EL TIEMPO, ARRIBA Y EN SU CUADRO. Solo en «Todo» y solo
                    si ha puesto sus horas: sin ellas no hay nada que colocar. */}
                {semana.horarios_puestos && filtro === 'todo' && (
                    <RejillaDeLaSemana semana={semana}
                        onAbrirDia={(fecha) => navigate(`/dashboard/semana/dia/${fecha}`)} />
                )}

                {/* Los siete días */}
                <div className="space-y-2.5" data-testid="mi-semana-dias">
                    {(semana.dias || []).map((dia, i) => (
                        <DiaCard key={dia.fecha} dia={dia} indice={i} filtro={filtro}
                            esHoy={dia.fecha === semana.hoy} onIr={() => irAlDia(dia.fecha)}
                            onVerElDia={() => navigate(`/dashboard/semana/dia/${dia.fecha}`)}
                            horariosPuestos={Boolean(semana.horarios_puestos)} conEntreno={conEntreno}
                            hayQueRepetir={hayQueRepetir} objetivo={objetivos[dia.fecha]} />
                    ))}
                </div>

                {/* QUÉ ES CADA COLOR DE LA TIRA (artefacto, ficha 20): él la dibuja con su
                    leyenda debajo -- «comidas · intra y post · entreno · cardio pautado ·
                    sueño · cardio extra» --, y sin ella son siete rayas de colores que no
                    explican nada. Va una sola vez al pie de la lista, no en cada día.

                    Sin el sueño, que necesita horas y las horas se quedaron fuera. Y solo
                    en «Todo», que es donde se pinta la tira. */}
                {/* La leyenda de la tira SIN horas, para el que todavía no las ha puesto:
                    la del tiempo vive arriba, con su rejilla. */}
                {filtro === 'todo' && !semana.horarios_puestos && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 px-1"
                        data-testid="leyenda-de-la-tira">
                        {(semana.horarios_puestos
                            /* Los colores y el orden de SU leyenda: comidas · intra y post ·
                               entreno · cardio pautado · sueño · cardio extra. Sin azul: en
                               su dibujo no hay ni uno, todo es naranja, marrón y gris. */
                            ? [['bg-brand', 'Comidas'], ['bg-[#6b5340]', 'Intra y post'],
                               ['bg-brand', 'Entreno'], ['border border-brand/80', 'Cardio pautado'],
                               ['bg-muted-foreground/25', 'Sueño'], ['bg-brand/70', 'Cardio extra']]
                            : [['bg-muted-foreground/35', 'Comidas'], ['bg-amber-600/60', 'Intra y post'],
                               ['bg-brand', 'Entreno'], ['bg-sky-500/70', 'Cardio'],
                               ['bg-sky-500/40', 'Se movió más']]
                        ).map(([color, que]) => (
                            <span key={que} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                <span className={`w-3.5 h-1.5 rounded-full ${color}`} />
                                {que}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </Wrap>
    );
};

/** LO QUE HIZO ESE DÍA (ficha 11 del artefacto «Mi semana»): «una señal pequeña a la
 *  derecha de cada día: ✓ si cerró el día, y el peso si se pesó. Sin colores ni riña: los
 *  que no, en blanco». Más las estrellas de su check-in (ficha 22).
 *
 *  Iba en una columna propia y a 390 px rompía la fila: se quedaba sin ancho el centro y el
 *  peso acababa pintado encima del estado. Ahora va en la línea de la tira, en horizontal y
 *  compacto, que es donde no le quita sitio a nada.
 */
const LoQueHizo = ({ dia, enLinea = false }) => {
    // LAS ESTRELLAS DEL DÍA, DE VUELTA (Francisco, 12-09, después de quitarlas: «vuelve a
    // poner el check-in dentro del día, ahí es donde el usuario valoraba»). Manda lo más
    // reciente.
    //
    // Se quitaron porque no había ninguna: el cierre había dejado de preguntarlas el 31-08
    // y, encima, el servidor las pedía por `mood`, la carita de mayo, en vez de por
    // `sensaciones`, que es donde el cierre las guarda. Arregladas las dos cosas, la ficha
    // 22 se sostiene: «cada día de la semana lleva sus estrellas al lado».
    if (!dia.cerrado && dia.peso == null && dia.estrellas == null && !dia.movimiento) return null;
    return (
        <div className={`flex items-center gap-2 flex-shrink-0 text-muted-foreground ${enLinea ? 'mt-1' : ''}`}
            data-testid={`hizo-${dia.fecha}`}>
            {/* EL VERDE, SOLO CON EL DÍA COMPLETO (Jesús, 11-09, punto 16): el ✓ dice «cerró
                el día», que es otra cosa que «la dieta está terminada», y en verde se leía
                como un aprobado. */}
            {dia.cerrado && (
                <span className={`text-sm leading-none ${dia.is_cuadrado === true ? 'text-emerald-400' : ''}`}
                    title="Cerró el día" data-testid={`cerrado-${dia.fecha}`}>✓</span>
            )}
            {/* SI SE MOVIÓ MÁS O MENOS DE LO NORMAL (ficha 26): «en la línea de cada día,
                una señal pequeña de si se movió». Es su propia respuesta del cierre, que
                estaba guardada y no se enseñaba en ningún sitio. El «igual» no se pinta:
                lo normal no es noticia, y con siete flechas iguales no se ve nada. */}
            {(dia.movimiento === 'mas' || dia.movimiento === 'menos') && (
                <span className={`text-xs leading-none ${
                        dia.movimiento === 'mas' ? 'text-brand' : 'text-muted-foreground/70'}`}
                    title={dia.movimiento === 'mas'
                        ? 'Ese día se movió más de lo normal' : 'Ese día se movió menos de lo normal'}
                    data-testid={`movimiento-${dia.fecha}`}>
                    {dia.movimiento === 'mas' ? '↑' : '↓'}
                </span>
            )}
            {dia.estrellas != null && (
                <span className="text-xs leading-none text-brand whitespace-nowrap"
                    title={`Se sintió ${dia.estrellas} de 5`}
                    data-testid={`estrellas-${dia.fecha}`}>
                    {'★'.repeat(dia.estrellas)}{'☆'.repeat(5 - dia.estrellas)}
                </span>
            )}
            {dia.peso != null && (
                <span className="text-xs font-data whitespace-nowrap"
                    data-testid={`peso-${dia.fecha}`}>{num1(dia.peso)} kg</span>
            )}
        </div>
    );
};

const DiaCard = ({ dia, indice, filtro, esHoy, onIr, onVerElDia, hayQueRepetir, objetivo,
                   horariosPuestos = false, conEntreno = true }) => {
    const descanso = dia.tipo_dia === 'descanso';
    // En «Entreno», los días de descanso se APAGAN en vez de desaparecer, para que la
    // semana no se descoloque (lo pide el doc tal cual).
    const apagado = filtro === 'entreno' && descanso;

    return (
        <div onClick={onIr} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onIr(); } }}
            data-testid={`dia-${dia.fecha}`}
            /* EL DÍA SIN CREAR, CON EL BORDE DISCONTINUO (su maqueta: «el viernes, que no
               está creado, con el borde discontinuo»). Es la misma señal que usa para el
               día sin planificar en el horario: lo que todavía no existe se dibuja con la
               línea cortada, sin necesidad de leer una palabra. */
            className={`surface p-3.5 sm:p-4 cursor-pointer transition-all
                hover:border-brand/40 ${esHoy ? 'border-brand/60' : ''} ${apagado ? 'opacity-40' : ''} ${
                dia.estado === 'sin_montar' ? 'border-dashed' : ''}`}>
            {/* LA BARRA DEL DÍA VA DEBAJO Y A LO ANCHO, no dentro de la columna del texto
                (12-09). En su dibujo ocupa la fila entera, y por eso se lee de un vistazo
                «el jueves entrena a mediodía»: en una columna estrecha las horas no caen
                donde dice la escala de arriba, que es justo lo que la hace servir. */}
            <div className="flex items-center gap-3 sm:gap-4">

            {/* Etiqueta del día: Lun 24 (+ pastilla Hoy) */}
            <div className="w-14 sm:w-16 flex-shrink-0 text-center">
                <p className={`text-[11px] font-bold uppercase tracking-wider ${esHoy ? 'text-brand' : 'text-muted-foreground'}`}>{DIAS_CORTOS[indice]}</p>
                <p className={`font-heading text-2xl font-bold leading-none ${esHoy ? 'text-brand' : 'text-foreground'}`}>{diaDelMes(dia.fecha)}</p>
                {esHoy && <span className="inline-block mt-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-brand text-white" data-testid={`hoy-${dia.fecha}`}>Hoy</span>}
            </div>

            {/* Las dos líneas del día */}
            <div className="flex-1 min-w-0 space-y-1">
                {filtro !== 'entreno' && <LineaDieta dia={dia} objetivo={objetivo} />}
                {filtro !== 'dieta' && conEntreno && <LineaEntreno dia={dia} descanso={descanso} />}
                {/* EL CARDIO QUE LE PAUTAN, EN SU FILA (ficha 24). Antes no salía en ninguna
                    parte: la app leía un campo de la rutina que no escribe nadie, así que el
                    cliente no se enteraba de que tenía cardio. Ahora lo pauta su entrenador
                    y aquí se le dice qué día le toca. */}
                {filtro !== 'dieta' && dia.cardio && (
                    <div className="flex items-center gap-2 text-sm" data-testid={`cardio-${dia.fecha}`}>
                        <Waves className="w-3.5 h-3.5 text-sky-400 flex-shrink-0" />
                        <span className="text-muted-foreground">
                            Cardio{dia.cardio.minutos ? ` · ${dia.cardio.minutos} min` : ''}
                            {dia.cardio.como ? ` ${dia.cardio.como}` : ''}
                        </span>
                    </div>
                )}
                {/* La tira del día (ficha 20). Solo en «Todo»: en los filtros de Dieta y
                    Entreno la fila habla de una cosa, y la tira las mezcla.

                    Y LO QUE HIZO VA AQUÍ, NO EN UNA COLUMNA PROPIA (revisión del 12-09).
                    Como columna se comía el ancho del centro y a 390 px la fila se rompía:
                    el peso se pintaba encima de la palabra («Sin crea75 kgr») y el nombre
                    de la rutina se quedaba en «Pectoral y tríc...», «Dorsa...» o «H». Aquí
                    abajo, al lado de la tira, no le quita sitio a nadie. */}
                {/* LO QUE HIZO, SIEMPRE DEBAJO Y NUNCA EN COLUMNA (12-09). Con el ✓ y el
                    peso a secas cabía a un lado; con las estrellas y la flecha del
                    movimiento ya no, y a 390 px le robaba el ancho al centro: los macros
                    partidos en tres líneas y las estrellas encima del «50,3». Debajo van
                    las cuatro señales seguidas y se leen de corrido. */}
                <LoQueHizo dia={dia} enLinea />
            </div>

            {/* La acción: llevar a Nutrición en ese día */}
            <div className="flex-shrink-0 flex items-center gap-2">
                {filtro !== 'entreno' && dia.estado === 'empezada' && (
                    <span className="text-xs font-bold uppercase tracking-wider text-brand" data-testid={`accion-${dia.fecha}`}>Terminar</span>
                )}
                {/* LAS DOS SALIDAS DEL DÍA SIN CREAR (ficha 19 del artefacto «Mi semana»,
                    Jesús 6-09-2026): «Crear y Repetir. Repetir es un atajo, no la única
                    puerta». Aquí salía una o la otra, nunca las dos, así que el que quería
                    empezar el día de cero no tenía botón.

                    «Crear» va delante y en naranja, que es la acción de verdad; «Repetir»
                    detrás y en gris, que es el atajo. Y solo sale cuando hay algo que
                    repetir -- un día con comidas de esta semana o de antes (ficha 17 del
                    parte de Gonzalo) --: en la cuenta recién abierta prometía repetir la
                    nada. Las dos llevan al mismo sitio, que es Nutrición en ese día, donde
                    están las tres puertas con sus nombres. */}
                {filtro !== 'entreno' && dia.estado === 'sin_montar' && (
                    /* LOS DOS ROTULOS, UNO DEBAJO DE OTRO EN EL TELÉFONO (revisión del
                       12-09). En fila se llevaban unos 120 px de los 390, y lo que se
                       quedaba sin sitio era la columna del medio: el nombre de la rutina
                       caía en tres renglones («Dorsal / y / bíceps»). Apilados liberan ese
                       ancho y la fila no crece, porque a su lado ya hay dos líneas. */
                    <span className="flex flex-col items-end sm:flex-row sm:items-baseline gap-0.5 sm:gap-2"
                        data-testid={`accion-${dia.fecha}`}>
                        <span className="text-xs font-bold uppercase tracking-wider text-brand">Crear</span>
                        {hayQueRepetir && (
                            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                                data-testid={`repetir-${dia.fecha}`}>Repetir</span>
                        )}
                    </span>
                )}
                {/* «VER DÍA» ABRE EL DÍA, EN TODOS LOS FILTROS (Francisco, 13-09: «veo el
                    botón de ver día en entreno y también redirige a nutrición, ese es el
                    problema»).
                    
                    Era un rótulo pintado, no un botón: quien mandaba era la fila entera, y
                    la fila abre la dieta. Así que el texto decía una cosa y hacía otra, y en
                    el filtro Entreno además no venía a cuento. Ahora es un botón de verdad y
                    lleva al día por dentro, que es lo que su nombre promete. La fila, por
                    fuera de él, sigue llevando a la dieta en «Todo» y en «Dieta», que es
                    donde el cliente va a crearla o terminarla. */}
                {filtro !== 'entreno' && dia.estado === 'montada' && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); onVerElDia(); }}
                        className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground"
                        data-testid={`accion-${dia.fecha}`}>Ver día</button>
                )}
                {/* Y EN EL FILTRO ENTRENO TAMBIÉN (ficha 10 del artefacto «Mi semana»):
                    «cada fila se queda en LUN 31 · Entreno y desaparece el Ver día, así que
                    desde ahí no se puede entrar a ningún sitio».

                    Entrar sí se entraba -- la fila entera es pulsable y la flecha seguía
                    ahí --, pero sin rótulo no lo parecía, y una fila que no parece pulsable
                    es una fila rota para quien la mira. El día de descanso no lo lleva: está
                    apagado a propósito con ese filtro y un rótulo lo devolvería a primer
                    plano. */}
                {filtro === 'entreno' && !descanso && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); onVerElDia(); }}
                        className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground"
                        data-testid={`accion-${dia.fecha}`}>Ver día</button>
                )}
                <ChevronRight className="w-4 h-4 text-foreground/40" />
            </div>
            </div>

            {/* LA BARRA SE VA ARRIBA, A LA REJILLA (12-09): aquí estorbaba, porque la
                fila necesita el ancho para el estado, los macros y los botones. Al que
                todavía no ha puesto sus horas le queda la tira del orden de siempre, que es
                mejor que dejar el día mudo. */}
            {/* LA TIRA DEL ORDEN, solo para el que todavía no ha puesto sus horas: con
                horario, lo que se mira es la rejilla de arriba. */}
            {filtro === 'todo' && !horariosPuestos && (
                    <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                            <TiraDelDia bloques={dia.bloques} />
                        </div>
                    </div>
                )}
        </div>
    );
};

// Por dónde se sale el día y por cuánto, el que más lejos primero: solo lo que pasa del
// margen de 4 de la casa. Sin objetivo (todavía no ha llegado, o falló) no hay lista.
/** LOS TRES MACROS, EN EL MISMO GRIS (ficha 01 del artefacto, y Francisco el 12-09).
 *
 *  «Cada día enseña sus tres macros y cada uno lleva su color: 120,9 en naranja · 77,4 en
 *  azul · 61,6 en amarillo. Cómo tiene que quedar: sin color, los tres en el mismo gris. Es
 *  la misma decisión que tomaste esta mañana para Nutrición.»
 *
 *  Aquí estuvieron un rato de colores, por un «faltan los colores» mío mal entendido: lo
 *  que faltaba eran las CIFRAS, no los colores. Las cifras se quedan; el color, no.
 */
const GRIS_DEL_MACRO = 'text-muted-foreground';

const desviosDelDia = (macros, objetivo) => {
    if (!objetivo) return [];
    // SIEMPRE EN EL MISMO ORDEN: proteína, hidratos, grasa (Jesús, 11-09, punto 16).
    //
    // Iban ordenados por tamaño del desvío, así que cada fila los enseñaba en un orden
    // distinto -- «+38 H · +31 P», luego «−19 P · +8 G», luego «+35 H · +35 P» -- y para
    // comparar dos días había que leer las letras una por una. El orden de la casa es el
    // de siempre y no depende de los números.
    return ['P', 'H', 'G']
        .map(k => ({ k, d: (macros?.[k] || 0) - (objetivo[k] || 0) }))
        .filter(x => Math.abs(x.d) > MARGEN);
};

const LineaDieta = ({ dia, objetivo }) => {
    const m = dia.macros || {};
    // LAS CIFRAS DEL DÍA, QUE ES LO QUE PINTA SU ARTEFACTO (Francisco, 12-09).
    //
    //     «Las semanas en la imagen del artefacto muestran los macros y nosotros no.»
    //
    // Aquí vivía la decisión 6 de Jesús del 8-09 -- «solo el desvío, nunca las cifras»,
    // para que Mi semana no fuera un segundo sitio con los mismos números --, y su
    // artefacto es posterior y las dibuja día a día: «Cuadrada 120,9 · 77,4 · 61,6»,
    // «Incompleta 154,3 · 126,9 · 59,1». Ante la contradicción manda lo más reciente, y lo
    // más reciente es él.
    //
    // Van SIN COLOR, que eso no cambia (fichas 01 y 24 del parte de Gonzalo): los números
    // siempre en blanco y el color en la palabra del estado. Y el desvío se va con las
    // cifras: las dos cosas juntas llenan la línea de dígitos y dicen lo mismo dos veces.
    const cifras = ['P', 'H', 'G']
        .map(k => ({ k, v: m?.[k] }))
        .filter(x => typeof x.v === 'number');
    // «Sin cuadrar» y «Sin terminar» siguen siendo dos cosas distintas (Jesús, 11-09, punto
    // 15): al que le falta comida, terminar; al que se pasa, cuadrar. Eso no se ve en las
    // cifras, así que el desvío se sigue calculando aunque ya no se escriba.
    const sePasaElDia = dia.is_cuadrado === false
        && desviosDelDia(m, objetivo).some(x => x.d > 0);
    return (
        /* El icono NO envuelve: va fuera del grupo que salta de línea, alineado arriba,
           para que con dos renglones de texto no se quede solo en el primero. */
        <div className="flex items-start gap-2 text-sm min-w-0" data-testid={`dieta-${dia.fecha}`}>
            <Utensils className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-[3px]" />
            {dia.estado === 'montada' && (
                /* «creada» delante y sin `truncate` en ella: a 390 px la línea se cortaba
                   justo ahí y el día se quedaba en «99 · 82 · 32 · ...» (recorrido móvil
                   del 23-08).

                   Y LOS NÚMEROS TAMPOCO SE CORTAN (28-08). Llevaban `truncate`, y en el
                   teléfono no caben: medida la fila, son 354 px de los que el rótulo «Ver
                   día» se lleva 86 y el cuadrito del día 63, así que a los tres números les
                   quedaban 52 px para los 108 que ocupan. Resultado en pantalla: «Creada ·
                   118 …», que es justo el dato por el que se entra a mirar. Ahora, cuando no
                   caben, bajan enteros a la línea de abajo: la fila crece unos píxeles en el
                   móvil y no se pierde nada. */
                /* CREADA NO ES CUADRADA (revisión del 2-09).
                   Las siete filas decían «Creada» en verde, así que el día que se queda a
                   128 g de hidratos se leía igual que el que cuadra, y el verde le decía
                   que iba bien. El calendario de Nutrición ya distingue las dos cosas con
                   `is_cuadrado`; aquí se usa esa misma señal y no una nueva.

                   EL NARANJA DE «SIN CUADRAR» SE VA (Jesús, ficha 02 del artefacto «Mi
                   semana», 6-09-2026). Estaba SIN DECIDIR desde el 2-09 por chocar con el
                   punto 77 -- «naranja sólo para lo que se pasa»; un día a medias no es un
                   error --, y él lo cierra con esa misma regla: «sin cuadrar no es pasarse:
                   es que todavía no está ajustada. El naranja se reserva para lo que se pasa».
                   «Cuadrada» sí se queda en verde, «que eso sí es estar resuelto».

                   AQUÍ SÍ Y EN EL CALENDARIO DE NUTRICIÓN NO, y no es un olvido. Lo que
                   frenaba el cambio en el 2-09 era que `DietCalendar` pinta «Sin cuadrar» con
                   el mismo naranja y quedarían las dos pantallas diciendo lo mismo de dos
                   formas. Pero allí el estado lo dice UN PUNTO DE COLOR y nada más, y el gris
                   ya está cogido por «A medias»: dejar «Sin cuadrar» sin color allí funde dos
                   estados en uno. Aquí no pasa, porque cada fila lleva la palabra escrita.
                   Queda para él decidir qué color le toca en el calendario.

                   LOS NÚMEROS, EN BLANCO (ficha 24 del parte de Gonzalo, 5-09). Iban en
                   naranja / azul / amarillo (P / H / G), el único sitio de la app donde un
                   número llevaba el color de su macro. La regla de Jesús es la de siempre:
                   los números siempre en blanco; el color va en la palabra de estado y en la
                   barra. Con el número de la proteína en naranja al lado de «Sin cuadrar» en
                   naranja, además, parecía que la proteína era lo que fallaba. */
                <span className="font-data text-foreground flex items-baseline gap-1 flex-wrap min-w-0">
                    {/* `null` es «no hay con qué juzgarlo» (un cliente sin macros puestos):
                        ahí se dice «Creada» a secas y sin color, que es lo único cierto.
                        Antes cualquier cosa que no fuera `true` se leía «Sin cuadrar». */}
                    <span className={`font-semibold flex-shrink-0 ${
                        dia.is_cuadrado === true ? 'text-emerald-400' : 'text-muted-foreground'}`}
                        data-testid={`estado-dieta-${dia.fecha}`}>
                        {/* LAS MISMAS PALABRAS QUE EN LAS OTRAS TRES PANTALLAS (Jesús,
                            10-09): «tres estados y ninguno más: sin crear, sin terminar,
                            completo... se calcula una vez y se escribe igual en todas
                            partes». La señal ya era la misma (`is_cuadrado`); lo que no
                            coincidía eran las palabras, y un cliente que lee «Cuadrada»
                            aquí y «Día completo» en Nutrición no sabe si son lo mismo. */}
                        {/* Y TERMINAR NO ES CUADRAR (Jesús, 11-09, punto 15). Todo lo que
                            no estaba cuadrado se llamaba «Sin terminar», incluidos los días
                            que SE PASAN: el mismo día salía «sin terminar» en su fila y
                            contado «por cuadrar» arriba. Sus dos definiciones: al que le
                            falta comida, terminar; al que está hecho contra unos macros que
                            ya no son los suyos -- y por eso se pasa --, cuadrar. */}
                        {/* Y LAS PALABRAS SON LAS DE SU MAQUETA (12-09): «Cuadrada» e
                            «Incompleta». El 10-09 se habían igualado a las de Nutrición
                            -- «Día completo», «Te falta terminar» -- para que las cuatro
                            pantallas dijeran lo mismo, y su dibujo de «La semana en curso»
                            es posterior y usa estas dos. Manda lo más reciente.

                            «Sin cuadrar» se queda para el día que SE PASA, que eso no es
                            estar incompleto (Jesús, 11-09, punto 15): al que le falta
                            comida, terminar; al que se pasa, cuadrar. */}
                        {dia.is_cuadrado === true ? 'Cuadrada'
                            : dia.is_cuadrado === false
                                ? (sePasaElDia ? 'Sin cuadrar' : 'Incompleta')
                                : 'Creada'}
                    </span>
                    {/* Y LOS TRES MACROS DEL DÍA, CON SU LETRA Y REDONDOS: «Cuadrada 121P
                        · 77H · 62G». Dos encargos suyos del 16-09, en este orden: primero
                        «que queden redondeados, si dice 192,2 queda en 192 pero si dice
                        192,6 queda en 193», y después «devuelve las letras a los macros».

                        La letra es la regla de la casa desde el 8-09 -- pegada al número y
                        sin espacio, como en Inicio, en Extras y en las favoritas -- y aquí
                        nunca la había tenido: esta línea nació el 12-09 copiando su maqueta,
                        que los dibuja sueltos. Sin color, que los números van siempre en
                        blanco.

                        La décima no se pierde: sigue entera en el `title`, que es lo que
                        sale al dejar el ratón encima. Aquí es una lista de siete días de un
                        vistazo, no el sitio donde se afina un gramo.

                        Cada cifra es indivisible y el grupo salta de línea entero cuando no
                        cabe: en un solo `nowrap` los tres se salían por la derecha a 390 px
                        y quedaban pintados encima del «Ver día» de al lado. */}
                    {cifras.length > 0 && (
                        <>
                            {/* El separador solo donde los tres números caben al lado de la
                                palabra. A 390 px bajan de renglón y el «·» se quedaba solo
                                al final de la línea de arriba, colgando de «Completa». */}
                            <span className="hidden sm:inline text-muted-foreground">·</span>
                            <span className="w-full sm:w-auto font-data flex flex-wrap gap-x-1"
                                data-testid={`macros-${dia.fecha}`}
                                title={`${num1(m.P)} g de proteína · ${num1(m.H)} g de hidratos · ${num1(m.G)} g de grasa`}>
                                {cifras.map((x, i) => (
                                    <span key={x.k} className={`whitespace-nowrap ${GRIS_DEL_MACRO}`}>
                                        {num0(x.v)}{x.k}
                                        {i < cifras.length - 1 && (
                                            <span className="text-muted-foreground"> ·</span>
                                        )}
                                    </span>
                                ))}
                            </span>
                        </>
                    )}
                </span>
            )}
            {/* SIN PARTIRSE POR LA MITAD (revisión del 12-09). A 390 px, con los dos
                rótulos de la derecha comiéndose el ancho, «Sin crear» caía en dos renglones
                y «1 de 3 comidas» en «1 de 3 / comidas». Son tres palabras: o caben o baja
                entera la frase, pero no se parte una sola línea de estado. */}
            {dia.estado === 'empezada' && (
                <span className="text-foreground whitespace-nowrap">
                    {dia.n_comidas_con_alimentos} de {plural(dia.n_comidas_total, 'comida')}
                </span>
            )}
            {dia.estado === 'sin_montar' && (
                <span className="text-muted-foreground whitespace-nowrap">Sin crear</span>
            )}
        </div>
    );
};

const LineaEntreno = ({ dia, descanso }) => {
    const e = dia.entreno || {};
    // El grupo del día, si su rutina lo dice. Hay rutinas cuyo nombre es literalmente
    // «Entreno», y con ese no hay grupo que contar: salía «Entreno · Entreno».
    const grupo = (e.nombre || '').trim().toLowerCase() === 'entreno' ? '' : (e.nombre || '');
    return (
        /* `min-w-0` para que el nombre largo se ajuste dentro de su hueco en vez de
           desbordar: sin él, «Hombro y abdomen» se salía por la derecha y se pintaba encima
           del «Ver día» de al lado (revisión del 12-09). */
        <div className="flex items-center gap-2 text-sm min-w-0" data-testid={`entreno-${dia.fecha}`}>
            {descanso ? (
                <>
                    <Moon className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                    <span className="text-muted-foreground">Descanso</span>
                </>
            ) : dia.tipo_dia === 'entrenamiento' ? (
                <>
                    <Dumbbell className="w-3.5 h-3.5 text-brand flex-shrink-0" />
                    {/* EL NOMBRE DE LA RUTINA, ENTERO (revisión del 12-09). Con `truncate`
                        se quedaba en «Pectoral y tríc...», «Dorsa...» o «Homb...» a 390 px,
                        que es justo el dato por el que se mira esa línea. Ahora baja de
                        línea cuando no cabe: la fila crece unos píxeles y no se pierde nada.
                        Es el mismo criterio que ya se tomó con los números de los macros. */}
                    {/* «ENTRENO · TORSO», CON LAS DOS PARTES (artefacto, ficha 08). Iba solo
                        el grupo -- «Pectoral y tríceps» --, y el día sin nombre de rutina se
                        quedaba en «Entreno» a secas: dos filas que dicen lo mismo escritas
                        de dos formas. Con el «Entreno ·» delante se lee igual en las dos, y
                        el grupo, cuando lo hay, va detrás. */}
                    {/* «ENTRENO · TORSO», PERO SOLO DONDE CABE (artefacto, ficha 08).
                        A 390 px, con «Crear» y «Repetir» a la derecha, el prefijo se comía
                        el hueco del grupo y «Dorsal y bíceps» caía en tres renglones con el
                        «·» colgando solo en una línea. En el teléfono lo que dice «esto es
                        entreno» es la mancuerna de al lado, que está siempre; a partir de
                        640 px, donde sobra sitio, va la frase entera como él la escribe.
                        Y el día que no trae grupo se queda en «Entreno» a secas, que ahí
                        el prefijo ES el dato y no puede esconderse. */}
                    {grupo ? (
                        <>
                            <span className="hidden sm:inline text-muted-foreground flex-shrink-0">
                                Entreno ·
                            </span>
                            <span className="text-foreground min-w-0">{grupo}</span>
                        </>
                    ) : (
                        <span className="text-muted-foreground">Entreno</span>
                    )}
                    {/* Preparado para el registro de sesiones: cuando el servidor sepa si
                        el entreno se hizo, aquí se dice; con null no se inventa nada. */}
                    {e.hecho === true && <span className="text-xs text-emerald-400 font-semibold">hecho</span>}
                    {e.hecho === false && <span className="text-xs text-muted-foreground">pendiente</span>}
                </>
            ) : (
                /* «POR DECIDIR» ES QUE NADIE LO HA DICHO TODAVÍA, y desde el 5-09 tiene
                   salida: arriba está la pregunta de qué días entrena (ficha 06). Antes era
                   un callejón, porque el tipo del día solo se elegía al crear el día. */
                <>
                    <HelpCircle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-muted-foreground"
                        title="Marca arriba qué días entrenas y lo sabremos de antemano">Por decidir</span>
                </>
            )}
        </div>
    );
};

export default MiSemanaPage;
